import { useEffect, useState } from "react";
import { useGetIdentity } from "ra-core";
import { BellRing } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { isDemoMode, isRoleSandbox } from "@/lib/runtimeMode";
import { enablePushNotifications, hasEnabledPush } from "./pushClient";
import {
  mayShowPushPrompt,
  markPushPromptShown,
  dismissPushPrompt,
} from "./pushPromptPreference";
export function PushEnablePrompt() {
  const { identity } = useGetIdentity();
  const id = identity?.id;
  const [open, setOpen] = useState(false);
  const [never, setNever] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  useEffect(() => {
    setOpen(false);
    setNever(false);
    setMessage("");
    if (
      id == null ||
      identity?.disabled ||
      isRoleSandbox() ||
      !mayShowPushPrompt(id)
    )
      return;
    let active = true;
    hasEnabledPush()
      .then((enabled) => {
        if (active && enabled === false && mayShowPushPrompt(id)) {
          markPushPromptShown(id);
          setOpen(true);
        }
      })
      .catch(() => {
        /* A failed status request is not proof that notifications are off. */
      });
    return () => {
      active = false;
    };
  }, [id, identity?.disabled]);
  const close = () => {
    if (busy) return;
    if (id != null) dismissPushPrompt(id, never);
    setOpen(false);
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) close();
      }}
    >
      <DialogContent
        className="max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-3xl p-5 sm:p-6"
        onEscapeKeyDown={(e) => {
          if (busy) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (busy) e.preventDefault();
        }}
      >
        <BellRing className="size-8 text-primary" />
        <DialogTitle className="pr-6 text-xl">
          Включить уведомления?
        </DialogTitle>
        <DialogDescription>
          На этом устройстве уведомления CRM не включены. Включите их, чтобы
          получать сообщения о задачах, комментариях и приближении сроков, даже
          когда приложение закрыто.
        </DialogDescription>
        {isDemoMode() && (
          <p className="text-sm text-muted-foreground">
            В учебной базе отправка уведомлений отключена.
          </p>
        )}
        <label className="flex cursor-pointer items-center gap-3 py-2 text-sm">
          <input
            className="size-4 shrink-0 cursor-pointer accent-primary"
            type="checkbox"
            checked={never}
            disabled={busy}
            onChange={(e) => setNever(e.target.checked)}
          />
          Больше не спрашивать
        </label>
        {message && (
          <p role="status" className="text-sm text-destructive">
            {message}
          </p>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Button variant="outline" disabled={busy} onClick={close}>
            Нет
          </Button>
          <Button
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setMessage("");
              try {
                await enablePushNotifications();
                if (id != null) dismissPushPrompt(id, never);
                setOpen(false);
              } catch (error) {
                setMessage(
                  error instanceof Error
                    ? error.message
                    : "Не удалось включить уведомления. Попробуйте ещё раз.",
                );
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? "Включаем…" : "Включить"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Изменить настройки можно в «Кабинет» → «Мой профиль» →
          «Push-уведомления». Выбор «Больше не спрашивать» действует для вашей
          учётной записи в этом браузере.
        </p>
      </DialogContent>
    </Dialog>
  );
}
